import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { PlusCircle, Trash2, Loader2, Droplets } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';

const BanoFormModal = ({ isOpen, onClose, onSave, editingItem, prendasParametrizadas = [], loadingPrendas = false, insumosParametrizados = [], loadingInsumos = false }) => {
  const [formData, setFormData] = useState({
    nombre: '',
    descripcion: '',
    capacidad: 1,
    prendas: [],
    insumos: [],
    estado: 'Activo'
  });

  const [selectedPrendaId, setSelectedPrendaId] = useState('');
  const [prendaDescripcionExtra, setPrendaDescripcionExtra] = useState('');
  const [prendaCantidad, setPrendaCantidad] = useState(1);

  // Estado para insumos
  const [selectedInsumoId, setSelectedInsumoId] = useState('');
  const [insumoCantidad, setInsumoCantidad] = useState(1);
  const [insumoDescripcion, setInsumoDescripcion] = useState('');

  useEffect(() => {
    if (editingItem) {
      setFormData({
        nombre: editingItem.nombre || '',
        descripcion: editingItem.descripcion || '',
        capacidad: editingItem.capacidad || 1,
        prendas: editingItem.prendas || [],
        insumos: editingItem.insumos || [],
        estado: editingItem.estado || 'Activo'
      });
    } else {
      setFormData({
        nombre: '',
        descripcion: '',
        capacidad: 1,
        prendas: [],
        insumos: [],
        estado: 'Activo'
      });
    }
    setSelectedPrendaId('');
    setPrendaDescripcionExtra('');
    setPrendaCantidad(1);
    setSelectedInsumoId('');
    setInsumoCantidad(1);
    setInsumoDescripcion('');
  }, [editingItem, isOpen]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleAddPrenda = () => {
    if (!selectedPrendaId || prendaCantidad < 1) {
      return;
    }

    const prendaSeleccionada = prendasParametrizadas.find(p => p.id === selectedPrendaId);
    if (!prendaSeleccionada) {
      return;
    }

    // Verificar si la prenda ya está agregada
    const yaExiste = formData.prendas.some(p => p.nombre === prendaSeleccionada.nombre);
    if (yaExiste) {
      return;
    }

    const nuevaPrenda = {
      nombre: prendaSeleccionada.nombre,
      peso: prendaSeleccionada.peso,
      cantidad: parseInt(prendaCantidad),
      descripcion: prendaDescripcionExtra || prendaSeleccionada.descripcion
    };

    setFormData(prev => ({
      ...prev,
      prendas: [...prev.prendas, nuevaPrenda]
    }));

    setSelectedPrendaId('');
    setPrendaDescripcionExtra('');
    setPrendaCantidad(1);
  };

  const handleRemovePrenda = (index) => {
    setFormData(prev => ({
      ...prev,
      prendas: prev.prendas.filter((_, i) => i !== index)
    }));
  };

  const handleUpdateCantidadPrenda = (index, nuevaCantidad) => {
    setFormData(prev => ({
      ...prev,
      prendas: prev.prendas.map((prenda, i) => 
        i === index 
          ? { ...prenda, cantidad: parseInt(nuevaCantidad) || 1 }
          : prenda
      )
    }));
  };

  // Handlers para insumos
  const handleAddInsumo = () => {
    if (!selectedInsumoId || insumoCantidad < 1) return;

    const insumoSeleccionado = insumosParametrizados.find(i => i.id === selectedInsumoId);
    if (!insumoSeleccionado) return;

    const yaExiste = formData.insumos.some(i => i.nombre === insumoSeleccionado.nombre);
    if (yaExiste) return;

    const nuevoInsumo = {
      insumoId: insumoSeleccionado.id,
      nombre: insumoSeleccionado.nombre,
      cantidad: parseInt(insumoCantidad),
      descripcion: insumoDescripcion.trim() || insumoSeleccionado.descripcion,
      tipo: 'insumo'
    };

    setFormData(prev => ({
      ...prev,
      insumos: [...prev.insumos, nuevoInsumo]
    }));

    setSelectedInsumoId('');
    setInsumoCantidad(1);
    setInsumoDescripcion('');
  };

  const handleRemoveInsumo = (index) => {
    setFormData(prev => ({
      ...prev,
      insumos: prev.insumos.filter((_, i) => i !== index)
    }));
  };

  const handleUpdateCantidadInsumo = (index, nuevaCantidad) => {
    setFormData(prev => ({
      ...prev,
      insumos: prev.insumos.map((insumo, i) =>
        i === index
          ? { ...insumo, cantidad: parseInt(nuevaCantidad) || 1 }
          : insumo
      )
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.nombre || formData.capacidad < 1) {
      return;
    }

    onSave(formData);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {editingItem ? 'Editar Baño' : 'Nuevo Baño'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Información básica */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="nombre">Nombre *</Label>
              <Input
                id="nombre"
                value={formData.nombre}
                onChange={(e) => handleChange('nombre', e.target.value)}
                placeholder="Ej: Baño Principal"
                required
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="descripcion">Descripción</Label>
            <Textarea
              id="descripcion"
              value={formData.descripcion}
              onChange={(e) => handleChange('descripcion', e.target.value)}
              placeholder="Descripción del baño"
              rows={3}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="estado">Estado</Label>
            <Select value={formData.estado} onValueChange={(value) => handleChange('estado', value)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Activo">Activo</SelectItem>
                <SelectItem value="Inactivo">Inactivo</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Sección de prendas */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label className="text-lg font-semibold">Prendas Asociadas</Label>
              <span className="text-sm text-gray-500">
                {formData.prendas.length} prenda(s)
              </span>
            </div>

            {/* Formulario para agregar prenda */}
            <Card className="bg-gray-50">
              <CardContent className="pt-6">
                {loadingPrendas ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
                    <span className="ml-2 text-sm text-gray-600">Cargando prendas...</span>
                  </div>
                ) : prendasParametrizadas.length === 0 ? (
                  <div className="text-center py-4 text-sm text-gray-500">
                    No hay prendas parametrizadas disponibles. 
                    <br />
                    Por favor, crea tipos de prenda primero.
                  </div>
                ) : (
                  <div className="grid grid-cols-12 gap-3 items-end">
                    <div className="col-span-5 space-y-2">
                      <Label htmlFor="prenda-select" className="text-sm">Seleccionar Prenda</Label>
                      <Select value={selectedPrendaId} onValueChange={setSelectedPrendaId}>
                        <SelectTrigger id="prenda-select">
                          <SelectValue placeholder="Selecciona una prenda..." />
                        </SelectTrigger>
                        <SelectContent>
                          {prendasParametrizadas.map((prenda) => (
                            <SelectItem key={prenda.id} value={prenda.id}>
                              {prenda.nombre} ({prenda.peso} g)
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="col-span-2 space-y-2">
                      <Label htmlFor="prenda-cantidad" className="text-sm">Cantidad</Label>
                      <Input
                        id="prenda-cantidad"
                        type="number"
                        min="1"
                        value={prendaCantidad}
                        onChange={(e) => setPrendaCantidad(e.target.value)}
                        placeholder="1"
                      />
                    </div>

                    <div className="col-span-4 space-y-2">
                      <Label htmlFor="prenda-descripcion-extra" className="text-sm">Descripción adicional</Label>
                      <Input
                        id="prenda-descripcion-extra"
                        value={prendaDescripcionExtra}
                        onChange={(e) => setPrendaDescripcionExtra(e.target.value)}
                        placeholder="Opcional"
                      />
                    </div>

                    <div className="col-span-1">
                      <Button
                        type="button"
                        onClick={handleAddPrenda}
                        size="sm"
                        className="w-full"
                        disabled={!selectedPrendaId || prendaCantidad < 1}
                      >
                        <PlusCircle className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Lista de prendas agregadas */}
            {formData.prendas.length > 0 && (
              <div className="space-y-2">
                {formData.prendas.map((prenda, index) => (
                  <Card key={index} className="border-l-4 border-l-blue-500">
                    <CardContent className="p-3">
                      <div className="flex items-center justify-between gap-4">
                        <div className="flex-1">
                          <p className="text-sm font-medium">{prenda.nombre}</p>
                          <p className="text-xs text-gray-500">{prenda.descripcion || '-'}</p>
                        </div>
                        
                        <div className="flex items-center gap-3">
                          <div className="text-sm text-gray-600">
                            <span className="font-semibold">{prenda.peso} g</span>
                          </div>
                          
                          <div className="flex items-center gap-2">
                            <Label className="text-xs text-gray-600">Cant:</Label>
                            <Input
                              type="number"
                              min="1"
                              value={prenda.cantidad || 1}
                              onChange={(e) => handleUpdateCantidadPrenda(index, e.target.value)}
                              className="w-16 h-8 text-sm"
                            />
                          </div>
                          
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemovePrenda(index)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>

          {/* Sección de insumos */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label className="text-lg font-semibold flex items-center gap-2">
                <Droplets className="h-5 w-5 text-amber-600" />
                Insumos Asociados
              </Label>
              <span className="text-sm text-gray-500">
                {formData.insumos.length} insumo(s)
              </span>
            </div>

            {/* Formulario para agregar insumo */}
            <Card className="bg-amber-50 border-amber-200">
              <CardContent className="pt-6">
                {loadingInsumos ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="h-6 w-6 animate-spin text-amber-600" />
                    <span className="ml-2 text-sm text-gray-600">Cargando insumos...</span>
                  </div>
                ) : insumosParametrizados.length === 0 ? (
                  <div className="text-center py-4 text-sm text-gray-500">
                    No hay insumos parametrizados disponibles.
                    <br />
                    Por favor, crea consumibles primero.
                  </div>
                ) : (
                  <div className="grid grid-cols-12 gap-3 items-end">
                    <div className="col-span-5 space-y-2">
                      <Label htmlFor="insumo-select" className="text-sm">Seleccionar Insumo</Label>
                      <Select value={selectedInsumoId} onValueChange={setSelectedInsumoId}>
                        <SelectTrigger id="insumo-select">
                          <SelectValue placeholder="Selecciona un insumo..." />
                        </SelectTrigger>
                        <SelectContent>
                          {insumosParametrizados.map((insumo) => (
                            <SelectItem key={insumo.id} value={insumo.id}>
                              {insumo.nombre} {insumo.tipo ? `(${insumo.tipo})` : ''}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="col-span-2 space-y-2">
                      <Label htmlFor="insumo-cantidad" className="text-sm">Cantidad</Label>
                      <Input
                        id="insumo-cantidad"
                        type="number"
                        min="1"
                        value={insumoCantidad}
                        onChange={(e) => setInsumoCantidad(e.target.value)}
                        placeholder="1"
                      />
                    </div>

                    <div className="col-span-4 space-y-2">
                      <Label htmlFor="insumo-descripcion" className="text-sm">Descripción adicional</Label>
                      <Input
                        id="insumo-descripcion"
                        value={insumoDescripcion}
                        onChange={(e) => setInsumoDescripcion(e.target.value)}
                        placeholder="Opcional"
                      />
                    </div>

                    <div className="col-span-1">
                      <Button
                        type="button"
                        onClick={handleAddInsumo}
                        size="sm"
                        className="w-full bg-amber-600 hover:bg-amber-700"
                        disabled={!selectedInsumoId || insumoCantidad < 1}
                      >
                        <PlusCircle className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Lista de insumos agregados */}
            {formData.insumos.length > 0 && (
              <div className="space-y-2">
                {formData.insumos.map((insumo, index) => (
                  <Card key={index} className="border-l-4 border-l-amber-500">
                    <CardContent className="p-3">
                      <div className="flex items-center justify-between gap-4">
                        <div className="flex items-center gap-2 flex-1">
                          <Droplets className="h-4 w-4 text-amber-600" />
                          <div className="flex-1">
                            <p className="text-sm font-medium">{insumo.nombre}</p>
                            <p className="text-xs text-gray-500">{insumo.descripcion || '-'}</p>
                          </div>
                        </div>

                        <div className="flex items-center gap-3">
                          <div className="flex items-center gap-2">
                            <Label className="text-xs text-gray-600">Cant:</Label>
                            <Input
                              type="number"
                              min="1"
                              value={insumo.cantidad || 1}
                              onChange={(e) => handleUpdateCantidadInsumo(index, e.target.value)}
                              className="w-16 h-8 text-sm"
                            />
                          </div>

                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveInsumo(index)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancelar
            </Button>
            <Button type="submit">
              {editingItem ? 'Actualizar' : 'Crear'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default BanoFormModal;
